import React, { useState, useEffect, useContext } from "react";
import { Link } from "react-router-dom";
import { HorizontalBar } from 'react-chartjs-2';
import { Skeleton } from '@material-ui/lab';
import { Divider, Backdrop, CircularProgress } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { ConvertToCurrentTimeZone } from '../header/timeFormatter';
import { HttpService } from "../../apiAuthorization/httpService";
import { OfflineService } from "../../offline/storageService/offlineService";
import { GlobalTheme, TileTheme } from "../../layouts/theme";
import { Theme } from "../contexts/themeContext.js";
import { Context } from "../contexts/siteContext.js";
import Card from "../card/card.js";
import CardHeader from "../card/cardHeader.js";
import CardBody from "../card/cardBody.js";
import CardContent from '@material-ui/core/CardContent';

const useStyles = makeStyles(theme => ({
    backdrop: {
        zIndex: theme.zIndex.drawer + 1,
        color: "#fff",
        position: "absolute"
    }
}));

export default function StaffingChart(props) {
    const classes = useStyles();
    const tileClasses = TileTheme();
    const offlineService = new OfflineService();
    const { defaultSite, siteLoading } = useContext(Context);
    const { theme } = useContext(Theme);
    const [loading, setLoading] = useState(true);
    const [backDrop, setBackDrop] = useState(false);
    const [avgSignedIn, setAvgSignedIn] = useState(0)
    const [totalSignedIn, setTotalSignedIn] = useState(0);
    const [totalActiveEmployees, setTotalActiveEmployees] = useState(0);
    const [totalEmployeesOnBreak, setTotalEmployeesOnBreak] = useState(0);
    const [weekDayName, setWeekDayName] = useState();
    const currentTheme = (GlobalTheme[theme]);
    const date = ConvertToCurrentTimeZone(new Date(), "L", null, defaultSite);

    const getStaffingData = () => {
        if (defaultSite.length !== 0) {
            setBackDrop(true);
            HttpService.post("/Staffing/GetStaffingSummary",
            {
                houseCode: defaultSite.houseCode[0],
                siteId: defaultSite.id + "",
                workDate: date
            })
            .then(async (result) => {
                if (result && result.offline) {
                    let offlineResult = await offlineService.getOfflinesBySite(defaultSite.id, "StaffingSummary");
                    result = offlineResult.OfflineData;
                }
                else if (result && result.error) {
                    return;
                }
                else {
                    //online remove and add new
                    offlineService.removeOfflineBySite(defaultSite.id, "StaffingSummary");
                    offlineService.addOffline(defaultSite.id, "StaffingSummary", result);
                }

                let epaySourceDetails = result.apiData ? JSON.parse(result.apiData).data : null;
                let useEpay = result.useEpay;
                let staffing = result.data ? result.data[0] : null;

                if (staffing != null && useEpay != null) {
                    staffing.TotalSignedInEmployees = 0;
                    staffing.TotalActiveEmployees = 0;
                    staffing.TotalEmployeesOnBreak = 0;
                    if (!staffing.AvgSignedInEmployees) {
                        staffing.AvgSignedInEmployees = 0;
                    }
                    if (epaySourceDetails != null) {
                        epaySourceDetails.forEach((item, index) => {
                            if (!item.at_work && !item.at_break && !item.at_meal_break) {
                                item.Status = "";
                            }
                            else if (item.at_work && !item.at_break && !item.at_meal_break) {
                                item.Status = "Active";
                                staffing.TotalSignedInEmployees++;
                                staffing.TotalActiveEmployees++;
                            }
                            else {
                                //(item.at_work && !item.at_break && item.at_meal_break) || (item.at_work && item.at_break && !item.at_meal_break)
                                item.Status = "On Break";
                                staffing.TotalSignedInEmployees++;
                                staffing.TotalEmployeesOnBreak++;
                            }
                        });
                    }
                }

                if (staffing != null) {
                    setAvgSignedIn(staffing.AvgSignedInEmployees);
                    setTotalSignedIn(staffing.TotalSignedInEmployees);
                    setTotalActiveEmployees(staffing.TotalActiveEmployees);
                    setTotalEmployeesOnBreak(staffing.TotalEmployeesOnBreak);
                    setWeekDayName(staffing.WeekDayName);
                }
                setLoading(false);
                setBackDrop(false);
            },
            (error) => {
                console.log(error);
                setBackDrop(false);
            })
        }
    };

    const chartData = {
        labels: ["On Break", "Active"],
        datasets: [{
            label: '',
            data: [totalEmployeesOnBreak, totalActiveEmployees],
            backgroundColor: [
                '#7fb8e6',
                '#0a6ab7'
            ],
            borderWidth: 1
        }]
    };

    const options = {
        legend: {
            display: false
        },
        scales: {
            xAxes: [{
                ticks: { beginAtZero: true, precision: 0 }
            }]
        },
        responsive: true
    };

    useEffect(() => {
        if (!siteLoading)
            getStaffingData();
        // eslint-disable-next-line 
    }, [props.mode, defaultSite, siteLoading]);

    return (<>
        <div style={{ position: "relative" }}>
            <Link to={{ pathname: "/StaffingList" }}>
                <Backdrop className={classes.backdrop} id="staffingBackdrop" open={backDrop}>
                    <CircularProgress color="inherit" />
                </Backdrop>
                <Card chart color="primary" className={tileClasses.card} style={{ background: currentTheme.background, color: currentTheme.text }} >
                    <CardContent className={tileClasses.cardContent}>
                        <CardHeader>
                            <div className={tileClasses.title}>Staffing</div>
                            {loading === true ? (<Skeleton variant="rect" height={76} />) : (
                                <HorizontalBar data={chartData} options={options} height={76} />
                            )}
                        </CardHeader>
                        <Divider />
                        <CardBody>
                            <div className={tileClasses.subTitle}>Daily Summary</div>
                            {loading === true ? (<div><Skeleton variant="text" /><Skeleton variant="text" /></div>) : (
                                <div className="d-flex flex-column">
                                    <label className={tileClasses.optionText}>Week Day: {weekDayName}</label>
                                    <label className={tileClasses.optionText}>Avg: {avgSignedIn}</label>
                                    <label className={tileClasses.optionText}>In: {totalSignedIn}</label>
                                </div>
                            )}
                        </CardBody>
                    </CardContent>
                </Card>
            </Link>
        </div>
    </>
    );
}
